/**
 * WalletPicker.jsx
 *
 * Modal listing every EVM wallet detected via EIP-6963 (+ legacy
 * fallbacks) so the user picks explicitly which one to connect —
 * instead of the dapp silently grabbing whichever `window.ethereum`
 * happened to win the injection race. Wallet icons come straight from
 * each wallet's own EIP-6963 announcement (a data URI the extension
 * supplies at runtime); wallets found only via legacy fallback get a
 * plain text badge instead of a reconstructed logo.
 */
import React from 'react';
import GlowText from '../GlowText';

function WalletRow({ wallet, onSelect, busy }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(wallet.uid)}
      disabled={busy}
      className="btn-robotic btn-robotic-cyan w-full flex items-center gap-4 px-4 py-3 text-left normal-case tracking-normal"
      style={{ fontFamily: "'Rajdhani', sans-serif" }}
    >
      <span
        className="flex items-center justify-center w-10 h-10 shrink-0 overflow-hidden"
        style={{ border: '1px solid var(--steel-light)', borderRadius: 8, background: '#0a0f1c' }}
      >
        {wallet.icon ? (
          <img src={wallet.icon} alt="" className="w-full h-full object-contain" />
        ) : (
          <span className="font-mono-hud text-xs glow-cyan">{wallet.name.slice(0, 2).toUpperCase()}</span>
        )}
      </span>
      <span className="flex-1">
        <span className="block font-bold" style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 13, color: 'var(--text)' }}>
          {wallet.name}
        </span>
        <span className="block text-dim" style={{ fontSize: 11 }}>
          {wallet.source === 'eip6963' ? 'Detected' : 'Detected (legacy)'}
        </span>
      </span>
    </button>
  );
}

export default function WalletPicker({ wallets, onSelect, onClose, busy }) {
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: 'rgba(3,5,10,0.88)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
    >
      <div
        className="mech-panel mech-corners relative w-full max-w-sm"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mech-panel-header">
          <span className="status-dot" />
          <GlowText text="CONNECT WALLET" size={12} variant="cyan" />
        </div>

        <div className="p-5 space-y-3">
          {wallets.length === 0 ? (
            <div className="text-center py-6 space-y-2">
              <GlowText as="div" text="NO WALLET DETECTED" size={14} variant="amber" />
              <p className="font-mono-hud text-dim text-fluid-sm">
                Install MetaMask, Rabby, OKX Wallet, Phantom, or any other
                EVM wallet extension, then refresh this page.
              </p>
            </div>
          ) : (
            wallets.map((w) => (
              <WalletRow key={w.uid} wallet={w} onSelect={onSelect} busy={busy} />
            ))
          )}

          <button
            type="button"
            onClick={onClose}
            className="btn-robotic btn-robotic-ghost w-full px-4 py-2 text-xs"
          >
            CANCEL
          </button>
        </div>
      </div>
    </div>
  );
}
